import React from 'react';
import { useHistory } from 'react-router';
import {
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@medely/web-components';
import { Box, HStack, InfoBadge, Label } from '@medely/ui-kit';
import { ChevronRight } from '@medely/web-components/icons';
import { SocialProofBanner } from '@mp/components/socialProofBanner';
import { useCurrentUser } from '@mp/hooks';

export enum OnboardingStepName {
  Address = 'address',
  Credentials = 'credentials',
  SkillsAssessments = 'skills_assessments',
  BackgroundCheck = 'background_check',
  UniversalBackgroundCheck = 'universal_background_check',
  DirectDeposit = 'direct_deposit',
  Experience = 'experience',
  WorkExperience = 'work_experience',
}

export type OnboardingStep = {
  name: OnboardingStepName;
  label: string;
  hint?: string;
  path: string;
  isComplete: boolean;
  isAwaitingReview: boolean;
  disabled?: boolean;
};

type OnboardingListProps = {
  steps: OnboardingStep[];
  onLearnMoreClick?: () => void;
};

type OnboardingListStepBadgeProps = {
  step: OnboardingStep;
};

const OnboardingListStepBadge = ({ step }: OnboardingListStepBadgeProps) => {
  if (step.isComplete) {
    return (
      <Box mr={step.isAwaitingReview ? 1 : 0}>
        <InfoBadge
          color={step.isAwaitingReview ? 'info' : 'success'}
          label={step.isAwaitingReview ? 'In review' : 'Completed'}
        />
      </Box>
    );
  }

  if (step.hint) {
    return <InfoBadge color="default" label={step.hint} />;
  }

  return null;
};

const OnboardingList: React.FC<OnboardingListProps> = ({ steps, onLearnMoreClick }) => {
  const history = useHistory();
  const { isLoading } = useCurrentUser();
  const nextStep = steps.find((step) => !step.isComplete);

  if (!steps.length) {
    return null;
  }

  return (
    <>
      {nextStep && (
        <HStack mb={3} alignItems="center" justifyContent="space-between">
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push(nextStep.path)}
              data-testid="onboarding-list-get-started-button"
            >
              Get Started
            </Button>
          </Box>

          {onLearnMoreClick && (
            <Box onClick={onLearnMoreClick}>
              <Label size="m" color="state.teal.secondary">
                Learn more
              </Label>
            </Box>
          )}
        </HStack>
      )}

      <List disablePadding>
        {steps.map((step, index) => (
          <React.Fragment key={step.path}>
            {index > 0 && <Divider boxProps={{ sx: { width: '100%' } }} />}

            <ListItem
              disablePadding
              data-testid={
                step.isComplete
                  ? 'onboarding-list-completed-steps-item'
                  : 'onboarding-list-remaining-steps-item'
              }
            >
              <ListItemButton onClick={() => !step.disabled && history.push(step.path)}>
                <ListItemText
                  primary={<Label size="m">{step.label}</Label>}
                  data-testid={
                    step.isComplete
                      ? 'onboarding-list-completed-steps-item-title'
                      : 'onboarding-list-remaining-steps-label'
                  }
                />
                <Box px={2}>
                  <OnboardingListStepBadge step={step} />
                </Box>
                {!step.isAwaitingReview && <ChevronRight />}
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
      <SocialProofBanner waitForUser={!isLoading} />
    </>
  );
};

export default OnboardingList;
